/**
 * Dojo product-analytics event registry + a consent-safe capture helper.
 *
 * Single source of truth for the custom PostHog event names dojo emits — mirrors
 * the FEATURE_FLAGS registry pattern in ./feature-flags.ts so events are
 * referenced as constants (no stringly-typed drift) and the PostHog dashboards
 * are built against exactly these names.
 *
 * The conversion funnel these power:
 *   land in dojo ($pageview, autocaptured)
 *     → open Billing ($pageview /billing, autocaptured)
 *     → tick the right-of-withdrawal waiver  (API_PAYMENT_WAIVER_ACKNOWLEDGED)
 *     → start Stripe checkout for an API credit pack  ← THE target action
 *       (API_PAYMENT_INITIATED)
 *     → return from checkout (CHECKOUT_RETURNED; not proof of payment)
 *
 * Page-level steps come from the autocaptured `$pageview` (init uses
 * `capture_pageview: 'history_change'`), so only non-page *actions* live here.
 * "Payment for API access" is the only paid surface today; when more paid
 * services ship, add their conversion events here next to the API_PAYMENT_* set
 * and reuse the same dashboard pattern.
 *
 * Consent: capture() only fires when analytics cookie consent is present
 * (hasAnalyticsConsent) — the same gate that lets providers.tsx call
 * posthog.init(). That covers consent declined up front AND consent withdrawn
 * mid-session (the cookie flips to analytics:false), and avoids posthog-js
 * "called before init" drops. SDK readiness uses the same marker and init event
 * as providers.tsx; consented cold-mount actions wait briefly for initialization.
 */
import posthog from 'posthog-js'
import { hasAnalyticsConsent } from '@/lib/cookie-consent'
import { createConsentSafeCapture } from '../../../shared-types/posthog-privacy'

export const ANALYTICS_EVENTS = {
	// ── Conversion: payment for API access (the single target action today) ──
	//
	// Fired the instant a developer commits to buying an API credit pack and we
	// kick off Stripe checkout — BEFORE the redirect, so it captures intent even
	// when checkout itself fails (e.g. Stripe prod Prices not yet configured).
	// `kind` ('scan' | 'read') lets every report break the one conversion down by
	// which metered API surface is being bought.
	API_PAYMENT_INITIATED: 'api_payment_initiated',
	// A browser return flag is unverified, not a revenue or fulfillment event.
	CHECKOUT_RETURNED: 'checkout_returned',
	// Server-only: verified Stripe webhook, settled payment and persisted credits.
	API_PAYMENT_SUCCEEDED: 'api_payment_succeeded',
	// Checkout could not even start (BFF / Stripe error). Separates "couldn't
	// pay" (a bug/config gap) from "chose not to pay" (a product/pricing signal).
	API_PAYMENT_CHECKOUT_FAILED: 'api_payment_checkout_failed',
	// Ticked the EU right-of-withdrawal waiver — the gate that unlocks the buy
	// buttons. Drop-off between this and INITIATED isolates checkbox friction.
	API_PAYMENT_WAIVER_ACKNOWLEDGED: 'api_payment_waiver_acknowledged',
	// Opened a /gift/<campaign> link signed in. `status` is the server's answer
	// (claimed | already_claimed | expired | not_eligible), `campaign` the slug.
	GIFT_LINK_OPENED: 'gift_link_opened',
	ONBOARDING_SURVEY_COMPLETED: 'onboarding_survey_completed',
	ONBOARDING_SURVEY_SKIPPED: 'onboarding_survey_skipped',
	ONBOARDING_SURVEY_FAILED: 'onboarding_survey_failed',

	// ── Conversion: extension author/ownership claim reached VERIFIED ──────
	// Core target action (unlocks author-only features: analytics, finding
	// reviews, listing customization — free scans need no claim).
	// NOW EMITTED SERVER-SIDE by catalog-bff (routers/verification.router.ts →
	// lib/posthog-capture.ts) so ALL methods (DNS_TXT / MANIFEST_EMAIL /
	// EMAIL_PIN) and ALL users are counted — including consent-declined users
	// the client gate can't reach. Kept here as the canonical event-name +
	// property reference (extension_id, store, method) the dashboards are built
	// against; dojo no longer captures it.
	EXTENSION_OWNERSHIP_VERIFIED: 'extension_ownership_verified',

	// ── API-key intent (a key is the prerequisite for using the paid API) ──
	API_KEY_COPIED: 'api_key_copied',
	API_KEY_ROTATED: 'api_key_rotated',

	// ── BYO analytics: connecting the author's own provider ───────────────
	// The target action is a connected source (it is what makes the dashboard
	// exist at all), so both outcomes of the attempt are recorded: SUCCEEDED
	// carries `provider`, FAILED carries `provider` + `reason` (the router's
	// error CODE, never the raw message) so the funnel shows whether authors
	// are losing to a wrong key, a wrong region, or an unverified listing.
	POSTHOG_CONNECTED: 'posthog_connected',
	POSTHOG_CONNECT_FAILED: 'posthog_connect_failed',
	// Someone actually opened a dashboard — the retention half of the feature.
	// Carries `extension_id` and `has_data`.
	EXTENSION_ANALYTICS_VIEWED: 'extension_analytics_viewed',

	// ── Demand signal: voting for an in-development /tools utility ──
	// Two-step funnel, both carrying a `feature` property (the tool slug) so
	// reports can rank demand per tool and measure open→submit conversion:
	//   OPENED   = clicked a locked tool card (intent — the DB never sees this)
	//   SUBMITTED = actually cast the vote (also written to the DB)
	TOOL_VOTE_DIALOG_OPENED: 'tool_vote_dialog_opened',
	TOOL_VOTE_SUBMITTED: 'tool_vote_submitted',

	// ── Extenshi Pro M0: willingness-to-pay on the project page ──
	// All three carry `{surface, projectId}`. Consent-gated via capture().
	//   VIEWED  = a locked teaser panel mounted (one event per surface)
	//   NOTIFY  = clicked "Notify me" on a teaser (FeatureVote slug `pro-project`)
	//   UPSELL  = a competitor/project-limit dialog opened
	PRO_TEASER_VIEWED: 'pro_teaser_viewed',
	PRO_NOTIFY_CLICKED: 'pro_notify_clicked',
	PRO_UPSELL_SHOWN: 'pro_upsell_shown',
} as const

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS]

const captureWhenReady = createConsentSafeCapture({
	hasConsent: hasAnalyticsConsent,
	isReady: () => posthog.__loaded,
	send: (event, properties, timestamp) => posthog.capture(event, properties, { timestamp }),
})

/** Optional additive checkout metadata; old clients and non-consenting buyers omit it. */
export function createCheckoutAnalytics(): { consent: true; attemptId: string } | undefined {
	try {
		if (typeof window === 'undefined' || !hasAnalyticsConsent()) return undefined
		return { consent: true, attemptId: crypto.randomUUID() }
	} catch {
		// Unsupported telemetry APIs must never prevent the purchase itself.
		return undefined
	}
}

/**
 * Consent-safe wrapper around posthog.capture.
 *
 * No-op on the server and whenever the user has not granted analytics cookie
 * consent (see cookie-consent.ts) — the same gate as posthog.init() in
 * providers.tsx. Always route custom dojo events through here so the consent
 * gate lives in one place.
 */
export function capture(event: AnalyticsEvent, properties?: Record<string, unknown>): void {
	try {
		captureWhenReady(event, { ...properties, schema_version: 2 })
	} catch {
		// Analytics must never prevent the user action from completing.
	}
}
